'use client';

import React, { useState } from 'react';
import SlideWrapper from '../SlideWrapper';
import AppLogo from '@/components/ui/AppLogo';
import { Phone, Mail, ArrowRight, Leaf, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const FacebookIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
  </svg>
);

const InstagramIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
    <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
  </svg>
);

interface Props {
  id: string;
  registerRef: (id: string, el: HTMLElement | null) => void;
}

export default function Slide28CTA({ id, registerRef }: Props) {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error('Vui lòng nhập email của bạn');
      return;
    }
    // Backend integration point: POST /api/contact with { email }
    setSubmitted(true);
    toast.success('Cảm ơn bạn! Chúng tôi sẽ liên hệ sớm nhất có thể.');
  };

  return (
    <SlideWrapper id={id} registerRef={registerRef} className="bg-[#0f2318]">
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0f2318] via-[#1a3a2a]/60 to-[#0a2510]" />
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[rgba(232,160,48,0.5)] to-transparent" />

        {/* Large decorative elements */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1000px] h-[1000px] rounded-full bg-[rgba(76,175,125,0.03)] blur-3xl" />
        <svg className="absolute bottom-0 left-0 opacity-[0.06] w-[500px] h-[500px]" viewBox="0 0 200 200" fill="none">
          <ellipse cx="60" cy="160" rx="25" ry="70" fill="#4caf7d" transform="rotate(-15 60 160)" />
          <ellipse cx="120" cy="140" rx="20" ry="60" fill="#a8d87a" transform="rotate(10 120 140)" />
        </svg>
        <svg className="absolute top-0 right-0 opacity-[0.05] w-[400px] h-[400px]" viewBox="0 0 200 200" fill="none">
          <ellipse cx="150" cy="50" rx="20" ry="60" fill="#e8a030" transform="rotate(30 150 50)" />
        </svg>

        {/* Grid pattern */}
        <div
          className="absolute inset-0 opacity-[0.025]"
          style={{
            backgroundImage: 'linear-gradient(rgba(76,175,125,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(76,175,125,0.5) 1px, transparent 1px)',
            backgroundSize: '80px 80px',
          }}
        />
      </div>

      <div className="relative z-10 max-w-screen-xl mx-auto px-6 lg:px-16 xl:px-24 py-24 pt-28 min-h-screen flex flex-col justify-center">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left: Main CTA */}
          <div>
            <div className="fade-in-up mb-6">
              <div className="flex items-center gap-3">
                <AppLogo size={40} />
                <div>
                  <p className="font-display font-700 text-[16px] text-[#f0ede6]">Tri An Green Travel</p>
                  <p className="font-body text-[12px] text-[#4caf7d]">Hành trình xanh từ Trị An</p>
                </div>
              </div>
            </div>

            <div className="fade-in-up mb-3">
              <h2 className="font-display font-900 text-[clamp(32px,5vw,60px)] leading-[1.05] text-[#f0ede6] tracking-tight">
                Đồng hành cùng
              </h2>
              <h2 className="font-display font-900 text-[clamp(32px,5vw,60px)] leading-[1.05] tracking-tight text-gradient-green">
                Tri An Green
              </h2>
              <h2 className="font-display font-900 text-[clamp(32px,5vw,60px)] leading-[1.05] tracking-tight text-gradient-amber">
                Travel
              </h2>
            </div>

            <div className="fade-in-up mb-8">
              <div className="w-[60px] h-[3px] bg-gradient-to-r from-[#4caf7d] to-[#e8a030] rounded-full" />
            </div>

            <div className="fade-in-up mb-8">
              <p className="font-body text-[16px] lg:text-[18px] text-[#a0b8a8] leading-relaxed">
                Sẵn sàng mang chương trình Tri An Green Volunteer Camp đến trường của bạn? Hãy để chúng tôi kể thêm về hành trình này.
              </p>
            </div>

            <div className="fade-in-up space-y-3 mb-10">
              {[
                'Tư vấn miễn phí — không ràng buộc',
                'Lên kế hoạch chi tiết phù hợp với lịch học',
                'Hỗ trợ truyền thông và tuyển sinh viên',
              ].map((item) => (
                <div key={`cta-check-${item}`} className="flex items-center gap-3">
                  <CheckCircle2 size={18} className="text-[#4caf7d] flex-shrink-0" />
                  <span className="font-body text-[14px] lg:text-[15px] text-[#f0ede6]/80">{item}</span>
                </div>
              ))}
            </div>

            <div className="fade-in-up flex flex-wrap gap-4">
              <a
                href="tel:+840827092079"
                className="flex items-center gap-2.5 px-6 py-3.5 rounded-xl bg-gradient-to-r from-[#4caf7d] to-[#2d7a52] text-[#0f2318] font-display font-700 text-[15px] transition-all duration-200 hover:shadow-lg hover:shadow-[rgba(76,175,125,0.3)] hover:-translate-y-0.5 active:scale-95"
              >
                <Phone size={16} />
                Liên hệ ngay
              </a>
              <a
                href="mailto:triangreentravel@gmail.com"
                className="flex items-center gap-2.5 px-6 py-3.5 rounded-xl border border-[rgba(76,175,125,0.4)] text-[#4caf7d] font-display font-600 text-[15px] transition-all duration-200 hover:bg-[rgba(76,175,125,0.1)] hover:-translate-y-0.5 active:scale-95"
              >
                <Mail size={16} />
                Gửi email
              </a>
            </div>
          </div>

          {/* Right: Contact form + info */}
          <div className="space-y-6 fade-in-up">
            <div className="glass-card p-7 lg:p-8">
              <h3 className="font-display font-700 text-[18px] text-[#f0ede6] mb-2">
                Để lại email — nhận thông tin chi tiết
              </h3>
              <p className="font-body text-[13px] text-[#a0b8a8] mb-6">
                Chúng tôi sẽ gửi bộ tài liệu đầy đủ về chương trình trong vòng 24 giờ
              </p>

              {!submitted ? (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-[12px] font-600 text-[#a0b8a8] uppercase tracking-wider mb-2 font-body">
                      Email của bạn
                    </label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="ten@truong.edu.vn"
                      className="w-full px-4 py-3 rounded-xl bg-[rgba(255,255,255,0.05)] border border-[rgba(76,175,125,0.2)] text-[#f0ede6] font-body text-[14px] placeholder-[#a0b8a8]/40 focus:outline-none focus:border-[rgba(76,175,125,0.5)] focus:bg-[rgba(76,175,125,0.05)] transition-all duration-200"
                    />
                  </div>
                  <button
                    type="submit"
                    className="w-full flex items-center justify-center gap-2.5 py-3.5 rounded-xl bg-gradient-to-r from-[#e8a030] to-[#c47a10] text-[#0f2318] font-display font-700 text-[15px] transition-all duration-200 hover:shadow-lg hover:shadow-[rgba(232,160,48,0.3)] hover:-translate-y-0.5 active:scale-95"
                  >
                    Nhận tài liệu ngay
                    <ArrowRight size={16} />
                  </button>
                </form>
              ) : (
                <div className="text-center py-6">
                  <CheckCircle2 size={40} className="text-[#4caf7d] mx-auto mb-3" />
                  <p className="font-display font-700 text-[17px] text-[#f0ede6] mb-2">Đã nhận!</p>
                  <p className="font-body text-[13px] text-[#a0b8a8]">
                    Chúng tôi sẽ gửi tài liệu đến {email} trong vòng 24 giờ
                  </p>
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="glass-card p-5">
                <p className="text-[11px] font-600 uppercase tracking-wider text-[#a0b8a8] font-body mb-2">Hotline</p>
                <p className="font-display font-700 text-[15px] text-[#f0ede6]">0827 092 079</p>
                <p className="font-body text-[12px] text-[#a0b8a8] mt-1">T2–T7 · 8:00–18:00</p>
              </div>
              <div className="glass-card p-5">
                <p className="text-[11px] font-600 uppercase tracking-wider text-[#a0b8a8] font-body mb-2">Email</p>
                <p className="font-display font-600 text-[13px] text-[#f0ede6] break-all">triangreentravel@gmail.com</p>
              </div>
            </div>

            <div className="glass-card p-5">
              <p className="text-[11px] font-600 uppercase tracking-wider text-[#a0b8a8] font-body mb-3">
                Theo dõi chúng tôi
              </p>
              <div className="flex items-center gap-3">
                {[
                  { icon: <FacebookIcon size={16} />, label: 'Facebook', handle: 'Tri An Green Travel' },
                  { icon: <InstagramIcon size={16} />, label: 'Instagram', handle: '@triangreentravel' },
                ].map((social) => (
                  <div
                    key={`social-${social.label}`}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[rgba(76,175,125,0.1)] border border-[rgba(76,175,125,0.2)] flex-1"
                  >
                    <span className="text-[#4caf7d]">{social.icon}</span>
                    <span className="font-body text-[12px] text-[#a0b8a8] truncate">{social.handle}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="fade-in-up mt-16 pt-8 border-t border-[rgba(76,175,125,0.1)] flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Leaf size={14} className="text-[#4caf7d]" />
            <p className="font-body text-[12px] text-[#a0b8a8]">
              © 2026 Tri An Green Travel. Bảo vệ thiên nhiên — Trao giá trị thật.
            </p>
          </div>
          <p className="font-body text-[12px] text-[#a0b8a8]/60 text-center">
            Tri An Green Volunteer Camp · Hồ Trị An, Đồng Nai
          </p>
        </div>
      </div>
    </SlideWrapper>
  );
}